import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Button, Drawer, Typography, Space, Tabs, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text, Paragraph } = Typography;

function ConversationsTab() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getChatConversations(page, 30);
      setItems(data.conversations || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (e) {
      message.error('خطا در دریافت گفتگوها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(1); }, [fetchList]);

  const openDetail = async (record) => {
    setSelected(record);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await adminAPI.getChatConversation(record._id);
      setDetail(data.conversation);
    } catch (e) {
      message.error('خطا در دریافت جزئیات گفتگو');
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    { title: 'زمان', dataIndex: 'updatedAt', width: 160, render: (v) => moment(v).format('YYYY-MM-DD HH:mm') },
    {
      title: 'کاربر',
      dataIndex: 'user',
      render: (u, r) => u ? <Text>{u.firstName} {u.lastName}</Text> : <Text type="secondary">مهمان ({r.guestId?.slice(0, 8) || '—'})</Text>
    },
    { title: 'عنوان', dataIndex: 'title', render: (t, r) => <a onClick={() => openDetail(r)}>{t || 'بدون عنوان'}</a> },
    { title: 'مدل', dataIndex: 'model', width: 140, render: (m) => <Tag>{m || '—'}</Tag> },
    { title: 'توکن مصرفی', dataIndex: 'tokensUsed', width: 120 }
  ];

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        extra={<Button icon={<ReloadOutlined />} onClick={() => fetchList(pagination.page)}>بروزرسانی</Button>}
      >
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: 30,
            total: pagination.total,
            showSizeChanger: false,
            onChange: (p) => fetchList(p)
          }}
        />
      </Card>

      <Drawer
        title="جزئیات گفتگو"
        open={!!selected}
        onClose={() => setSelected(null)}
        width={640}
        loading={detailLoading}
      >
        {detail && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {(detail.messages || []).map((m, idx) => (
              <div key={idx} style={{
                background: m.role === 'user' ? '#f0f5ff' : '#fffbe6',
                padding: 12,
                borderRadius: 8
              }}>
                <Text strong>{m.role === 'user' ? 'کاربر' : 'دامبلدور'}</Text>
                <Paragraph style={{ marginBottom: 0, marginTop: 4, whiteSpace: 'pre-wrap' }}>{m.content}</Paragraph>
              </div>
            ))}
          </Space>
        )}
      </Drawer>
    </div>
  );
}

function ErrorsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getChatErrors(50);
      setLogs(data.logs || []);
    } catch (e) {
      message.error('خطا در دریافت لاگ خطاهای چت');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const columns = [
    { title: 'زمان', dataIndex: 'createdAt', width: 160, render: (v) => moment(v).format('YYYY-MM-DD HH:mm') },
    { title: 'پیام خطا', dataIndex: 'message', render: (m) => <Text type="danger">{m}</Text> },
    { title: 'مدل', dataIndex: 'model', width: 140 }
  ];

  return (
    <Card size="small" extra={<Button icon={<ReloadOutlined />} onClick={fetchLogs}>بروزرسانی</Button>}>
      <Table rowKey="_id" columns={columns} dataSource={logs} loading={loading} pagination={false} />
    </Card>
  );
}

export default function ChatLogsManager() {
  return (
    <Tabs
      items={[
        { key: 'conversations', label: 'گفتگوها', children: <ConversationsTab /> },
        { key: 'errors', label: 'خطاهای چت', children: <ErrorsTab /> }
      ]}
    />
  );
}
